/**
 * Single source of truth for site-wide SEO/GEO metadata.
 * Used by <Seo> component and consumed by per-page metadata.
 */

export const SITE_URL = "https://selfiecam.ai";
export const SITE_NAME = "Stori";
export const BRAND_NAME = "Stori";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/og-default.jpg`;
export const TWITTER_HANDLE = "@selfiecamai";

// Brand facts used in JSON-LD + LLM-citable content
export const BRAND_FACTS = {
  legalName: "Stori",
  foundingDate: "2013",
  founders: ["Tamer Soliman"],
  headquarters: { city: "Los Angeles", region: "CA", country: "US" },
  industry: "Marketing technology",
  category: "AI photo experience platform for events and venues",
  oneLiner:
    "Stori is an AI-powered selfie cam and photo experience platform that captures branded guest photos at live events and permanent venues, then automates lead capture, influencer detection, and marketing follow-up.",
  metrics: {
    interactions: "25 million+",
    eventsPowered: "10,000+",
    countriesActive: "27",
    foundedYear: 2013,
  },
  sameAs: [
    "https://www.linkedin.com/company/selfiecam-ai",
    "https://www.instagram.com/selfiecam.ai",
    "https://twitter.com/selfiecamai",
  ],
};

// Organization JSON-LD (sitewide)
export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND_NAME,
  legalName: BRAND_FACTS.legalName,
  url: SITE_URL,
  logo: `${SITE_URL}/og/logo-512.png`,
  foundingDate: BRAND_FACTS.foundingDate,
  founders: BRAND_FACTS.founders.map((n) => ({ "@type": "Person", name: n })),
  address: {
    "@type": "PostalAddress",
    addressLocality: BRAND_FACTS.headquarters.city,
    addressRegion: BRAND_FACTS.headquarters.region,
    addressCountry: BRAND_FACTS.headquarters.country,
  },
  description: BRAND_FACTS.oneLiner,
  sameAs: BRAND_FACTS.sameAs,
};

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

/**
 * Per-page SEO + JSON-LD blocks.
 * Each entry maps a route path → its full metadata.
 */
export const PAGE_SEO = {
  "/": {
    title: "Stori — AI Selfie Cam for Events & Venues",
    description:
      "Stori is the AI selfie cam for live events and permanent venues. Capture branded photos, detect influencers, sync leads, and automate marketing follow-up.",
    keywords:
      "AI selfie cam, AI photo booth, event photo booth software, branded photo experience, influencer detection, lead capture for events",
    ogType: "website",
    breadcrumbs: [{ name: "Home", url: SITE_URL }],
  },
  "/features": {
    title: "Why Us — A Decade Ahead of the Category | Stori",
    description:
      "Stori invented the AI selfie cam in 2013 and ships new features every week. Hardware-agnostic, real-time influencer detection, native CRM sync — see why it's not even close.",
    keywords:
      "why selfiecam, AI photo booth comparison, best AI photo booth, influencer detection software, hardware-agnostic photo booth",
    ogType: "website",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "Why Us", url: `${SITE_URL}/features` },
    ],
  },
  "/pricing": {
    title: "Pricing — Plans That Pay for Themselves in 24 Hours | Stori",
    description:
      "Transparent Stori pricing: Essential $50/mo, Signature $100/mo (most popular), Unlimited $300/mo. Software-only plans or turnkey event rental packages.",
    keywords:
      "Stori pricing, AI photo booth pricing, event photo booth cost, photo booth software pricing, event rental pricing",
    ogType: "website",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "Pricing", url: `${SITE_URL}/pricing` },
    ],
  },
  "/marketers": {
    title: "For Marketing Teams — Turn Guest Moments Into Outcomes | Stori",
    description:
      "Stori for marketers: identify VIP guests in real time, capture qualified leads, sync to your CRM, and automate follow-up — all from one branded photo experience.",
    keywords:
      "event marketing automation, CRM photo booth, lead capture photo booth, brand activation software, experiential marketing platform",
    ogType: "website",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "For Marketers", url: `${SITE_URL}/marketers` },
    ],
  },
  "/events": {
    title: "For Live Events — AI Photo Booth Activations | Stori",
    description:
      "Turnkey AI photo booth activations for live events, brand launches, festivals, and corporate summits. Delivered, set up, and managed by our team.",
    keywords:
      "event photo booth, AI photo booth rental, brand activation photo booth, festival photo booth, corporate event photo booth",
    ogType: "website",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "For Events", url: `${SITE_URL}/events` },
    ],
  },
  "/about": {
    title: "About Stori — Inventors of the AI Selfie Cam (Est. 2013)",
    description:
      "Stori invented the AI selfie cam in 2013. Built by hospitality operators and AI researchers, we power 25M+ guest interactions across 27 countries.",
    keywords:
      "about Stori, AI selfie cam inventor, Tamer Soliman, AI photo experience company",
    ogType: "website",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "About", url: `${SITE_URL}/about` },
    ],
  },
  "/glossary": {
    title: "Glossary — AI Photo Booth Terms Explained | Stori",
    description:
      "Plain-language definitions: AI selfie cam, branded photo booth, influencer detection, orbital 360, UGC, lead capture, and more — by Stori.",
    keywords:
      "AI selfie cam definition, what is an AI photo booth, photo booth glossary, influencer detection definition",
    ogType: "article",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "Glossary", url: `${SITE_URL}/glossary` },
    ],
  },
  "/compare": {
    title: "Stori vs Simple Booth, Snappic, Hue, Curate, Photobooth Supply Co",
    description:
      "How Stori compares to Simple Booth, Snappic, Hue Photo Lounge, Curate, and Photobooth Supply Co on AI, influencer detection, automation, and pricing.",
    keywords:
      "Stori vs Simple Booth, Stori vs Snappic, Stori vs Hue, photo booth software comparison, best AI photo booth",
    ogType: "article",
    breadcrumbs: [
      { name: "Home", url: SITE_URL },
      { name: "Compare", url: `${SITE_URL}/compare` },
    ],
  },
};
